import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Add, Close } from '@mui/icons-material';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Skeleton,
  Tooltip,
} from '@mui/material';
import Navbar from '../../shared/Navbar';
import SprintStepper from './components/SprintStepper';
import ProjectSummaryCard from './components/ProjectSummaryCard';
import SprintModal from '../../../components/SprintModal';
import TicketRow from '../../../components/TicketRow';
import { fetchProjectDropdownsByUser, setFilters } from '../../../store/ticketSlice';
import { fetchSprintTimeline, fetchScrumRole } from '../../../store/scrumSlice';
import {
  FILTER_VALUES,
  PROJECT_TYPES,
  SPRINT_TIMELINE_STATUS,
  SPRINT_TICKET_LIST_TYPES,
  TASK_STATUS,
  BACKEND_SCRUM_ROLES,
} from '../../../shared/appConstants';
import { api, ENDPOINTS } from '../../../services';
import { reportStyles } from '../../../shared/globalSX';
import './SprintTimeline.scss';

const DEFAULT_STATUS_FILTERS = {
  Completed: true,
  Active: true,
  Draft: true,
};

const MS_PER_DAY = 24 * 60 * 60 * 1000;
const MAX_AUTO_PAGINATION_CALLS = 3;

const getCalendarDateMs = (dateString) => {
  if (!dateString) return null;

  const [datePart] = String(dateString).split('T');
  const [year, month, day] = datePart.split('-').map(Number);

  if (!year || !month || !day) {
    const parsedDate = new Date(dateString);
    return Number.isNaN(parsedDate.getTime()) ? null : Date.UTC(
      parsedDate.getFullYear(),
      parsedDate.getMonth(),
      parsedDate.getDate()
    );
  }

  return Date.UTC(year, month - 1, day);
};

const getStartsInDays = (startDate) => {
  const startMs = getCalendarDateMs(startDate);
  if (startMs === null) return 0;

  const today = new Date();
  const todayMs = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());

  return Math.max(0, Math.ceil((startMs - todayMs) / MS_PER_DAY));
};

const SprintTimelineSkeleton = () => (
  <div className="sprint-timeline-content skeleton-wrapper">
    <div className="left-pane">
      <div className="sprint-stepper">
        {[1, 2, 3].map(i => (
          <div key={i} className="stepper-item">
            <div className="stepper-indicator status-draft" />
            <div className="sprint-card">
               <div className="card-header">
                 <div>
                   <Skeleton variant="text" width={120} height={24} />
                   <Skeleton variant="text" width={200} height={16} />
                 </div>
                 <div className="chips-container">
                   <Skeleton variant="rectangular" width={60} height={24} className="skeleton-chip" />
                   <Skeleton variant="rectangular" width={80} height={24} className="skeleton-chip" />
                 </div>
               </div>
               <div className="card-details">
                 <Skeleton variant="text" width={140} height={20} className="skeleton-mt-1" />
                 <Skeleton variant="rectangular" width={100} height={32} className="skeleton-mt-2 skeleton-rounded-lg" />
               </div>
            </div>
          </div>
        ))}
      </div>
    </div>
    <div className="right-pane">
      <div className="summary-card">
        <h3>
          <Skeleton variant="text" width={200} height={32} />
        </h3>
        <hr className="summary-separator" />
        <div className="summary-list">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="summary-item">
              <Skeleton variant="text" width={80} height={20} />
              <Skeleton variant="text" width={40} height={36} />
            </div>
          ))}
        </div>
      </div>
      <div className="velocity-card">
        <div className="header-row">
          <Skeleton variant="text" width={140} height={24} />
        </div>
        <hr />
        <div className="velocity-stats">
          <div className="sp-count-container">
            <Skeleton variant="text" width={80} height={40} />
          </div>
        </div>
      </div>
      <div className="filters-card">
        <div className="filters-header">
          <h3><Skeleton variant="text" width={120} height={24} /></h3>
        </div>
      </div>
    </div>
  </div>
);

const SprintTimeline = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { projectDropdownsByUser, projectDropdownsByUserLoading, filters } = useSelector((state) => state.tickets);
  const {
    sprintTimeline,
    sprintTimelineLoading,
    sprintSummary,
    sprintSummaryLoading,
    scrumRole
  } = useSelector((state) => state.scrum);
  const { user } = useSelector(state => state.auth);

  const [statusFilters, setStatusFilters] = useState(DEFAULT_STATUS_FILTERS);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [isSprintModalOpen, setIsSprintModalOpen] = useState(false);
  const [isSprintTicketsModalOpen, setIsSprintTicketsModalOpen] = useState(false);
  const [projectsRequested, setProjectsRequested] = useState(false);
  const [selectedProjectInitialized, setSelectedProjectInitialized] = useState(false);
  const [selectedSprintForTickets, setSelectedSprintForTickets] = useState(null);
  const [selectedSprintTickets, setSelectedSprintTickets] = useState([]);
  const [selectedSprintTicketsLoading, setSelectedSprintTicketsLoading] = useState(false);
  const [selectedSprintTicketsError, setSelectedSprintTicketsError] = useState('');
  const [selectedSprintTicketListType, setSelectedSprintTicketListType] = useState(SPRINT_TICKET_LIST_TYPES.ALL);
  const scrollContainerRef = useRef(null);
  const loadingMoreRef = useRef(false);
  const scrolledToActiveRef = useRef(false);
  const autoPaginatingRef = useRef(false);

  const selectedProjectId = filters?.selectedProject;

  const selectedProject = useMemo(() => {
    if (!projectDropdownsByUser || !selectedProjectId || String(selectedProjectId).toLowerCase() === 'all') {
      return null;
    }

    return projectDropdownsByUser.find((project) => String(project.id) === String(selectedProjectId)) || null;
  }, [projectDropdownsByUser, selectedProjectId]);

  const isScrumMaster = scrumRole 
    ? scrumRole.scrum_role === BACKEND_SCRUM_ROLES.SCRUM_MASTER 
    : (selectedProject?.scrum_master?.id === user?.id || selectedProject?.scrum_master === user?.id);

  const projectId = selectedProject?.id || '';

  const mapSprintTicketRow = (ticket) => ({
    ticket_number_prefix: ticket?.project_code || selectedProject?.code || '',
    ticket_number: ticket?.ticket_number,
    title: ticket?.title || '',
    assignees: Array.isArray(ticket?.assignee) ? ticket.assignee : [],
    assignee_name: Array.isArray(ticket?.assignee) ? ticket.assignee[0] || '' : ticket?.assignee || '',
    due_date: ticket?.due_date || null,
    status: ticket?.status || '',
    priority: ticket?.priority || '',
  });

  useEffect(() => {
    dispatch(fetchProjectDropdownsByUser());
    setProjectsRequested(true);
  }, [dispatch]);

  useEffect(() => {
    if (!projectsRequested || selectedProjectInitialized || projectDropdownsByUserLoading) return;
    if (!Array.isArray(projectDropdownsByUser) || projectDropdownsByUser.length === 0) return;

    if (selectedProjectId && String(selectedProjectId).toLowerCase() !== 'all') {
      const projectExists = projectDropdownsByUser?.some((project) => String(project.id) === String(selectedProjectId));
      if (projectExists) {
        setSelectedProjectInitialized(true);
        return;
      }
    }

    const scrumProject = projectDropdownsByUser?.find((project) => (
      (project.project_type || '').toUpperCase() === PROJECT_TYPES.SCRUM.toUpperCase()
    ));

    if (scrumProject?.id) {
      dispatch(setFilters({ selectedProject: scrumProject.id }));
    } else if (!selectedProjectId || String(selectedProjectId).toLowerCase() !== String(FILTER_VALUES.ALL)) {
      dispatch(setFilters({ selectedProject: FILTER_VALUES.ALL }));
    }

    setSelectedProjectInitialized(true);
  }, [
    dispatch,
    projectsRequested,
    projectDropdownsByUser,
    projectDropdownsByUserLoading,
    selectedProjectId,
    selectedProjectInitialized
  ]);

  const activeStatusFilterList = useMemo(() => (
    Object.entries(statusFilters)
      .filter(([, isSelected]) => isSelected)
      .map(([status]) => status.toUpperCase())
  ), [statusFilters]);

  const statusQuery = useMemo(() => activeStatusFilterList.join(','), [activeStatusFilterList]);
  const selectedStatuses = useMemo(() => new Set(activeStatusFilterList), [activeStatusFilterList]);
  const completedStatus = SPRINT_TIMELINE_STATUS.COMPLETED.toUpperCase();
  const activeStatus = SPRINT_TIMELINE_STATUS.ACTIVE.toUpperCase();
  const draftStatus = SPRINT_TIMELINE_STATUS.DRAFT.toUpperCase();
  const hasCompleted = selectedStatuses.has(completedStatus);
  const hasActive = selectedStatuses.has(activeStatus);
  const hasDraft = selectedStatuses.has(draftStatus);
  const selectedStatusCount = selectedStatuses.size;
  const isCompletedOnly = hasCompleted && selectedStatusCount === 1;
  const isStatusFilteredEmptyState = selectedStatusCount > 0
    && selectedStatusCount < Object.keys(DEFAULT_STATUS_FILTERS).length;
  const canCreateSprint = isScrumMaster && !isCompletedOnly;

  const emptyStateMessageKey = (() => {
    if (hasCompleted && !hasActive && !hasDraft) return 'sprintTimeline.noCompletedSprints';
    if (hasActive && !hasCompleted && !hasDraft) return 'sprintTimeline.noActiveSprints';
    if (hasDraft && !hasCompleted && !hasActive) return 'sprintTimeline.noDraftSprints';
    if (hasActive && hasCompleted && !hasDraft) return 'sprintTimeline.noActiveOrCompletedSprints';
    if (hasActive && hasDraft && !hasCompleted) return 'sprintTimeline.noActiveOrDraftSprints';
    if (hasCompleted && hasDraft && !hasActive) return 'sprintTimeline.noCompletedOrDraftSprints';
    return 'sprintTimeline.noSprints';
  })();

  useEffect(() => {
    setPage(1);
  }, [projectId, statusQuery]);

  useEffect(() => {
    scrolledToActiveRef.current = false;
    autoPaginatingRef.current = false;
  }, [projectId]);

  useEffect(() => {
    if (projectId) {
      dispatch(fetchScrumRole(projectId));
    }
  }, [dispatch, projectId]);

  useEffect(() => {
    if (!projectId) return;

    dispatch(fetchSprintTimeline({
      projectId,
      type: 'TIMELINE',
      status: statusQuery,
      page,
      limit,
      append: page > 1
    }));
  }, [dispatch, projectId, statusQuery, page, limit]);

  useEffect(() => {
    if (!projectId) return;
    dispatch(fetchSprintTimeline({
      projectId,
      type: 'SUMMARY',
      page: 1,
      limit
    }));
  }, [dispatch, projectId, limit]);

  useEffect(() => {
    setIsSprintTicketsModalOpen(false);
    setSelectedSprintForTickets(null);
    setSelectedSprintTickets([]);
    setSelectedSprintTicketsError('');
    setSelectedSprintTicketsLoading(false);
    setSelectedSprintTicketListType(SPRINT_TICKET_LIST_TYPES.ALL);
  }, [projectId]);

  const timelineResponse = sprintTimeline || {};
  const summaryResponse = sprintSummary || {};
  const timelineSprints = useMemo(() => Array.isArray(timelineResponse.data) ? timelineResponse.data : [], [timelineResponse.data]);
  const timelineSprintsWithDerivedStartDays = useMemo(() => (
    timelineSprints.map((sprint) => ({
      ...sprint,
      starts_in_days: getStartsInDays(sprint?.start_date),
    }))
  ), [timelineSprints]);
  const activeSprint = summaryResponse.active_sprint || timelineResponse.active_sprint || null;
  const projectSummary = summaryResponse.project_summary || null;
  const activeSprintName = activeSprint?.name || '';
  const projectName = projectSummary?.project_name || selectedProject?.name || '';
  const showSprintDueDateColumn = selectedSprintTickets.some((ticket) => Boolean(ticket?.due_date));
  const totalSprintCount = Number(timelineResponse.total || 0);
  const limitFromApi = Number(timelineResponse.limit || limit);
  const hasMore = totalSprintCount > 0
    ? timelineSprints.length < totalSprintCount
    : timelineSprints.length > 0 && timelineSprints.length % limitFromApi === 0;
  const hasSprints = timelineSprints.length > 0;
  const isLoading = sprintTimelineLoading || sprintSummaryLoading || projectDropdownsByUserLoading;
  const isInitialLoad = (sprintTimeline === null || isLoading) && !hasSprints;

  useEffect(() => {
    if (!sprintTimelineLoading) {
      loadingMoreRef.current = false;
      autoPaginatingRef.current = false;
    }
  }, [sprintTimelineLoading]);

  // Auto-scroll to active sprint on load
  useEffect(() => {
    if (scrolledToActiveRef.current || sprintTimelineLoading || sprintTimeline === null) return;

    const activeIndex = timelineSprints.findIndex(s => (s.status || '').toUpperCase() === (SPRINT_TIMELINE_STATUS.ACTIVE || '').toUpperCase());

    if (activeIndex >= 0) {
      const tryScroll = (attempts = 0) => {
        const activeCard = document.getElementById(`sprint-card-${timelineSprints[activeIndex].id}`);
        const scrollContainer = scrollContainerRef.current;
        if (activeCard && scrollContainer) {
          const cardRect = activeCard.getBoundingClientRect();
          const containerRect = scrollContainer.getBoundingClientRect();
          const scrollTop = scrollContainer.scrollTop + (cardRect.top - containerRect.top) - (containerRect.height / 2) + (cardRect.height / 2);
          
          scrollContainer.scrollTo({
            top: scrollTop,
            behavior: 'smooth'
          });
          scrolledToActiveRef.current = true;
          autoPaginatingRef.current = false;
        } else if (attempts < 10) {
          setTimeout(() => tryScroll(attempts + 1), 100);
        } else {
          scrolledToActiveRef.current = true;
          autoPaginatingRef.current = false;
        }
      };
      tryScroll();
    } else if (hasMore && !autoPaginatingRef.current && page < MAX_AUTO_PAGINATION_CALLS) {
      autoPaginatingRef.current = true;
      loadingMoreRef.current = true;
      setPage(prev => prev + 1);
    } else if (!hasMore || page >= MAX_AUTO_PAGINATION_CALLS) {
      scrolledToActiveRef.current = true;
      autoPaginatingRef.current = false;
    }
  }, [timelineSprints, hasMore, sprintTimelineLoading, page, sprintTimeline]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return undefined;

    const handleScroll = () => {
      const isAtBottom = container.scrollHeight - container.scrollTop <= container.clientHeight + 10;

      if (!isAtBottom || !hasMore || sprintTimelineLoading || loadingMoreRef.current) {
        return;
      }

      loadingMoreRef.current = true;
      setPage((prev) => prev + 1);
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [hasMore, sprintTimelineLoading]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || !hasMore || sprintTimelineLoading || loadingMoreRef.current || timelineSprints.length === 0) {
      return;
    }

    if (container.scrollHeight <= container.clientHeight + 10) {
      loadingMoreRef.current = true;
      setPage((prev) => prev + 1);
    }
  }, [timelineSprints.length, hasMore, sprintTimelineLoading]);

  const handlePlanSprint = () => {
    setIsSprintModalOpen(true);
  };

  const handleSprintTicketCountClick = async (sprint, listType = SPRINT_TICKET_LIST_TYPES.ALL) => {
    if (!projectId || !sprint?.id) return;

    setSelectedSprintForTickets(sprint);
    setIsSprintTicketsModalOpen(true);
    setSelectedSprintTickets([]);
    setSelectedSprintTicketsError('');
    setSelectedSprintTicketsLoading(true);
    setSelectedSprintTicketListType(listType);

    try {
      const status = listType === SPRINT_TICKET_LIST_TYPES.BLOCKED
        ? TASK_STATUS.BLOCKED.toUpperCase()
        : '';
      const response = await api.get(ENDPOINTS.SCRUM_SPRINT_TICKETS(projectId, sprint.id, status));
      const rows = Array.isArray(response?.data?.data)
        ? response.data.data
        : Array.isArray(response?.data)
          ? response.data
          : [];

      setSelectedSprintTickets(rows.map(mapSprintTicketRow));
    } catch (error) {
      setSelectedSprintTicketsError(
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error.message ||
        'Failed to load sprint tickets'
      );
    } finally {
      setSelectedSprintTicketsLoading(false);
    }
  };

  const handleBlockedTicketCountClick = (sprint) => {
    handleSprintTicketCountClick(sprint, SPRINT_TICKET_LIST_TYPES.BLOCKED);
  };

  const handleCloseSprintTicketsModal = () => {
    setIsSprintTicketsModalOpen(false);
  };

  const refreshTimelineData = async () => {
    if (!projectId) return;

    loadingMoreRef.current = false;
    setPage(1);

    await Promise.all([
      dispatch(fetchSprintTimeline({
        projectId,
        type: 'TIMELINE',
        status: statusQuery,
        page: 1,
        limit
      })),
      dispatch(fetchSprintTimeline({
        projectId,
        type: 'SUMMARY',
        page: 1,
        limit
      }))
    ]);
  };

  const handleSprintCreated = async () => {
    await refreshTimelineData();
    setIsSprintModalOpen(false);
  };

  return (
    <div className="dashboard">
      <Navbar />
      <main className="main sprint-timeline-main-container">
        <div className="sprint-timeline-page">
          <div className="header">
            <div className="title-section-wrapper">
              <div className={`active-sprint-indicator ${!activeSprint && !isInitialLoad ? 'no-active-sprint' : ''}`}>
                <div className="active-sprint-dot"></div>
                <span className="sprint-timeline-label">{t('sprintTimeline.title')}</span>
                <span className="active-sprint-name">
                  {isInitialLoad ? (
                    <Skeleton variant="text" width={120} sx={{ display: 'inline-block', verticalAlign: 'middle' }} />
                  ) : activeSprint ? (
                    t('sprintTimeline.activeLabel', { name: activeSprintName })
                  ) : (
                    t('sprintTimeline.noActiveSprint')
                  )}
                </span>
              </div>
              <div className="title-section">
                <h1>{t('sprintTimeline.title')}</h1>
              </div>
            </div>
            {canCreateSprint && (
              <Tooltip title={isLoading ? t('dashboard.loading', 'Loading...') : ''} arrow placement="bottom" slotProps={{ tooltip: { className: 'app-tooltip' } }}>
                <span>
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={handlePlanSprint}
                    disabled={!projectId || isLoading}
                    className="plan-sprint-btn"
                  >
                    {t('sprintTimeline.planSprint')}
                  </Button>
                </span>
              </Tooltip>
            )}
          </div>

          {isInitialLoad ? (
            <SprintTimelineSkeleton />
          ) : projectId ? (
            <div className="sprint-timeline-content">
              <div className="left-pane" ref={scrollContainerRef}>
                {hasSprints ? (
                  <SprintStepper
                    sprints={timelineSprintsWithDerivedStartDays}
                    onTicketCountClick={handleSprintTicketCountClick}
                    onBlockedCountClick={handleBlockedTicketCountClick}
                  />
                ) : (
                  <div className="sprint-timeline-empty-state">
                    <h2 className="empty-state-title">{t(emptyStateMessageKey)}</h2>
                    <p className="empty-state-desc">
                      {t(isStatusFilteredEmptyState
                        ? 'sprintTimeline.noSprintsForSelectedStatusFilters'
                        : 'sprintTimeline.noSprintsDescription')}
                    </p>
                    {canCreateSprint && (
                      <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={handlePlanSprint}
                        className="empty-state-plan-sprint-btn"
                      >
                        {t('sprintTimeline.planSprint')}
                      </Button>
                    )}
                  </div>
                )}
                {sprintTimelineLoading && hasSprints ? (
                  <div className="sprint-timeline-loading-more">
                    <CircularProgress size={24} />
                  </div>
                ) : null}
              </div>

              <div className="right-pane">
                <ProjectSummaryCard
                  summaryData={summaryResponse}
                  activeSprint={activeSprint}
                  projectName={projectName}
                  filters={statusFilters}
                  setFilters={setStatusFilters}
                />
              </div>
            </div>
          ) : (
            <div className="sprint-timeline-empty-state">
              <h2 className="empty-state-title">{t('common.selectProjectFirst', 'Please select a project to view its sprint timeline.')}</h2>
            </div>
          )}
        </div>
      </main>

      <SprintModal
        open={isSprintModalOpen}
        onClose={() => setIsSprintModalOpen(false)}
        projectId={projectId}
        onSuccess={handleSprintCreated}
      />

      <Modal
        open={isSprintTicketsModalOpen}
        onClose={handleCloseSprintTicketsModal}
      >
        <Box sx={reportStyles.ticketsModalBox} className="sprint-tickets-modal-paper">
          <Box sx={reportStyles.ticketsModalHeader} className="sprint-tickets-modal-header">
            <Box>
              <Typography variant="h6" className="sprint-tickets-modal-title">
                {selectedSprintForTickets?.name || t('sprintTimeline.title')}
              </Typography>
              <Typography variant="body2" className="sprint-tickets-modal-subtitle">
                {selectedSprintTicketListType === SPRINT_TICKET_LIST_TYPES.BLOCKED
                  ? t('sprintTimeline.blockedCount', { count: selectedSprintTickets.length })
                  : t('sprintTimeline.tickets', { count: selectedSprintTickets.length })}
              </Typography>
            </Box>
            <IconButton onClick={handleCloseSprintTicketsModal} size="small" aria-label="Close sprint tickets modal">
              <Close />
            </IconButton>
          </Box>

          <Box sx={reportStyles.ticketsModalContent} className="sprint-tickets-modal-content">
            {selectedSprintTicketsLoading ? (
              <Box className="sprint-tickets-modal-loading">
                <CircularProgress size={28} />
              </Box>
            ) : selectedSprintTicketsError ? (
              <Box className="sprint-tickets-modal-empty">
                <Typography color="error" variant="body2">
                  {selectedSprintTicketsError}
                </Typography>
              </Box>
            ) : selectedSprintTickets.length === 0 ? (
              <Box className="sprint-tickets-modal-empty">
                <Typography variant="body2" color="text.secondary">
                  {selectedSprintTicketListType === SPRINT_TICKET_LIST_TYPES.BLOCKED
                    ? t('sprintTimeline.noBlockedTickets')
                    : t('sprintTimeline.noTickets')}
                </Typography>
              </Box>
            ) : (
              <TableContainer className="sprint-tickets-table-container">
                <Table size="small" className="sprint-tickets-table">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, width: showSprintDueDateColumn ? '40%' : '50%', fontSize: '15px' }}>Title</TableCell>
                      <TableCell sx={{ fontWeight: 600, width: showSprintDueDateColumn ? '20%' : '25%', fontSize: '15px' }}>Assignee</TableCell>
                      {showSprintDueDateColumn ? (
                        <TableCell sx={{ fontWeight: 600, width: '15%', fontSize: '15px' }}>Due Date</TableCell>
                      ) : null}
                      <TableCell sx={{ fontWeight: 600, width: showSprintDueDateColumn ? '12.5%' : '17%', fontSize: '15px' }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 600, width: showSprintDueDateColumn ? '12.5%' : '18%', fontSize: '15px' }}>Priority</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedSprintTickets.map((ticket) => (
                      <TicketRow
                        key={`${ticket.ticket_number_prefix}-${ticket.ticket_number}`}
                        ticket={ticket}
                        showDueDate={showSprintDueDateColumn}
                      />
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </Box>
      </Modal>
    </div>
  );
};

export default SprintTimeline;
